import { DatasourceFacade } from '@mode/accounts/domain';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { isPresent } from '@mode/shared/util-js';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, shareReplay, switchMap } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { ToastService } from '@mode/shared/util-toast';
import { selectedQueryOrDefinitionDatasourceId } from '../reducers/selectors';

export default class DataSourceService {
  static $inject = ['RxStoreService', 'ToastService', 'DatasourceFacade'];

  selectedQueryDatasource$: Observable<DatasourceDomain.Datasource>;

  constructor(
    private store: AppStore,
    private alertService: ToastService,
    private datasourceFacade: DatasourceFacade
  ) {
    this.selectedQueryDatasource$ = combineLatest([
      this.datasourceFacade.datasources$,
      this.datasourceFacade.defaultDatasource$,
      this.store.select(selectedQueryOrDefinitionDatasourceId).pipe(filter(isPresent)),
    ]).pipe(
      map(([datasources, defaultDatasource, queryDatasourceId]) => {
        const datasource = datasources.find((ds) => ds.id === (queryDatasourceId as any));
        return datasource || defaultDatasource;
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    /**
     * Triggers alert messages for refreshing a datasource. The datasource
     * resource itself doesn't provide any information about the refresh state,
     * so it's all powered by ephemeral UI state in NgRx.
     */
    this.selectedQueryDatasource$
      .pipe(
        switchMap((datasource) => {
          return this.datasourceFacade.reportDatasourcesUI$.pipe(
            map((ui) => ui[datasource.token]),
            distinctUntilChanged()
          );
        }),
        pairwise(),
        filter(([prev, next]) => next != null)
      )
      .subscribe({
        next: ([prev, next]) => {
          if (prev?.refreshing && next.refreshError != null) {
            this.alertService.error('Update Failed');
          } else if (!prev?.refreshing && next.refreshing) {
            this.alertService.success('Refreshing');
          }
        },
      });
  }

  public refreshDatasource(datasource: DatasourceDomain.Datasource) {
    this.datasourceFacade.refreshDatasource(datasource.token);
  }
}
